/* Created by Interactive 63 - https://interactive63.com */
/**
 * A tiny expression evaluator for number fields: + - * / and parentheses, with unary signs and
 * decimals. It is a hand-written parser; nothing typed by the user is ever executed.
 */

type Token =
  { kind: 'number'; value: number } | { kind: 'op'; value: '+' | '-' | '*' | '/' | '(' | ')' };

/** Splits text into tokens, or returns null when it contains anything unexpected. */
function tokenise(text: string): Token[] | null {
  const tokens: Token[] = [];
  let index = 0;
  while (index < text.length) {
    const char = text.charAt(index);
    if (/\s/.test(char)) {
      index += 1;
    } else if ('+-*/()'.includes(char)) {
      tokens.push({ kind: 'op', value: char as '+' | '-' | '*' | '/' | '(' | ')' });
      index += 1;
    } else {
      const match = /^(?:\d+\.?\d*|\.\d+)/.exec(text.slice(index));
      if (!match) return null;
      tokens.push({ kind: 'number', value: Number(match[0]) });
      index += match[0].length;
    }
  }
  return tokens;
}

/**
 * Evaluates an arithmetic expression. Returns null for empty, malformed or non-finite input
 * (including division by zero) instead of throwing.
 */
export function evaluateMaths(text: string): number | null {
  const tokens = tokenise(text);
  if (!tokens || tokens.length === 0) return null;
  let position = 0;
  const state = { failed: false };

  /** Looks at the next token without consuming it. */
  const peek = (): Token | undefined => tokens[position];
  /** Consumes the next token when it is the given operator. */
  const accept = (op: string): boolean => {
    const token = peek();
    if (token?.kind === 'op' && token.value === op) {
      position += 1;
      return true;
    }
    return false;
  };

  /** primary := number | '(' expression ')' */
  const primary = (): number => {
    const token = peek();
    if (token?.kind === 'number') {
      position += 1;
      return token.value;
    }
    if (accept('(')) {
      const value = expression();
      if (!accept(')')) state.failed = true;
      return value;
    }
    state.failed = true;
    return 0;
  };

  /** unary := ('+' | '-') unary | primary */
  const unary = (): number => {
    if (accept('-')) return -unary();
    if (accept('+')) return unary();
    return primary();
  };

  /** term := unary (('*' | '/') unary)* */
  const term = (): number => {
    let value = unary();
    for (;;) {
      if (accept('*')) value *= unary();
      else if (accept('/')) value /= unary();
      else return value;
    }
  };

  /** expression := term (('+' | '-') term)* */
  const expression = (): number => {
    let value = term();
    for (;;) {
      if (accept('+')) value += term();
      else if (accept('-')) value -= term();
      else return value;
    }
  };

  const result = expression();
  if (state.failed || position !== tokens.length || !Number.isFinite(result)) return null;
  return result;
}
